import React, { useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import styled from '@nobia/zeus-components/lib/styled'
import ModalOverlay from '../overlay/ModalOverlay'
import { useTranslation } from '../../utils/useTranslation'
import RadioGroup from '../radio/RadioGroup'
import LocationAutocomplete from '../locationAutocomplete/LocationAutocomplete'
import { formatPrice, getInstallationPrice } from '../cards/utils'

const SubHeading = styled.p`
  font-family: ${({ theme }) => theme.font.preamble.fontFamily};
  font-style: light;
  font-size: 12px;
  line-height: 17px;
  color: #555;
  text-transform: uppercase;
  margin: 0 0 8px;
`

const Heading = styled.h3`
  font-weight: 500;
  font-size: 28px;
  line-height: 35px;
  margin: 0 0 8px;
`

const Description = styled.p`
  font-family: ${({ theme }) => theme.font.preamble.fontFamily};
  font-size: 16px;
  line-height: 22px;
  margin: 0 0 24px;
`

const FormGroup = styled.div`
  width: 100%;
  margin: 0 0 24px;
`

const Label = styled.p`
  font-family: ${({ theme }) => theme.font.preamble.fontFamily};
  font-weight: 550;
  font-size: 16px;
  line-height: 22px;
  margin: 0 0 8px;
  display: flex;
  align-items: top;
  gap: 5px;

  span {
    font-weight: 300;
  }
`

const Subtitle = styled.p`
  font-family: ${({ theme }) => theme.font.preamble.fontFamily};
  font-size: 16px;
  line-height: 22px;
  margin: 0 0 8px;
`

const PriceSection = styled.div`
  margin: 0 0 20px;
`

const PriceLabel = styled.p`
  font-family: ${({ theme }) => theme.font.preamble.fontFamily};
  font-weight: 300;
  font-size: 16px;
  line-height: 22px;
  margin: 0 0 8px;
  color: #00000099;
`

const PriceValue = styled.p`
  font-family: ${({ theme }) => theme.font.headings.fontFamily};
  font-size: 28px;
  line-height: 35px;
  margin: 0;
`

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`

const PrimaryButton = styled.button`
  padding: 15px 25px;
  width: 100%;
  background-color: ${({ theme }) => theme.colors.button};
  color: ${({ theme }) => theme.colors.white};
  border: none;
  border-radius: ${({ theme }) => theme.button.borderRadius || '3px'};
  font-family: ${({ theme }) => theme.font.preamble.fontFamily};
  font-weight: 450;
  font-size: 16px;
  cursor: pointer;
  transition: opacity 0.5s;

  &:hover {
    opacity: 0.8;
  }
`

const TextButton = styled.button`
  width: 100%;
  font-family: ${({ theme }) => theme.font.preamble.fontFamily};
  font-weight: 300;
  font-size: 16px;
  line-height: 22px;
  text-decoration: underline;
  background: none;
  border: none;
  cursor: pointer;
  text-align: center;
`

const InstallationModal = ({
  isOpen,
  onClose,
  onSaveAndClose,
  title,
  cardType,
  unitType,
  priceText,
  defmessage,
  selection,
}) => {
  const { country } = useTranslation()
  const { t } = useTranslation('app.components.installation')
  const [location, setLocation] = useState(selection?.installation?.location || '')
  const [highRise, setHighRise] = useState(selection?.installation?.highRise || false)
  const [parkingDifficult, setParkingDifficult] = useState(selection?.installation?.parkingDifficult || false)
  const [storeBand, setStoreBand] = useState(selection?.installation?.storeBand || '')

  const currentDomain = window.currentDomain || ''
  const hasChargedFee = highRise || parkingDifficult

  const installationPrice = useMemo(() => {
    if (!storeBand) return 0
    return getInstallationPrice(cardType, unitType, storeBand, hasChargedFee)
  }, [cardType, unitType, storeBand, hasChargedFee])

  const displayPrice = priceText !== '0 kr' ? priceText : defmessage
  const formattedPrice = installationPrice
    ? formatPrice({ price: installationPrice, country })
    : displayPrice

  const searchStoresByPlaceId = async (placeId) => {
    if (!placeId || !currentDomain) return

    try {
      const url = `${currentDomain}/stores/searchcloseststoresbyplaceid?placeId=${encodeURIComponent(
        placeId
      )}`

      const response = await fetch(url)

      if (!response.ok) {
        console.error('Failed to search stores:', response.statusText)
        return
      }

      const data = await response.json()
      const store = data?.stores?.[0]

      if (store?.StoreBand) {
        setStoreBand(store.StoreBand)
      }
    } catch (error) {
      console.error('Error searching stores:', error)
    }
  }

  const handleLocationSelect = (locationItem) => {
    setLocation(locationItem?.name || '')

    if (locationItem?.placeId) {
      searchStoresByPlaceId(locationItem.placeId)
    }
  }

  const handleLocationChange = (value) => {
    setLocation(value)
    setStoreBand('')
  }

  const handleHighRiseChange = (value) => {
    setHighRise(!value)
  }

  const handleParkingDifficultChange = (value) => {
    setParkingDifficult(value)
  }

  const handleSaveAndClose = () => {
    onSaveAndClose({
      storeBand,
      hasChargedFee,
      location,
      highRise,
      parkingDifficult,
    })
  }

  const handleClose = () => {
    onClose({
      storeBand,
      hasChargedFee,
      location,
      highRise,
      parkingDifficult,
    })
  }

  if (!isOpen) {
    return null
  }

  return (
    <ModalOverlay onClose={handleClose} showTopRight={true}>
      <SubHeading>{t('title')}</SubHeading>
      <Heading>{title}</Heading>
      <Description>{t('description')}</Description>

      <FormGroup>
        <Label>
          {t('postal-code')}
          <span> ({t('postal-code-info')})</span>
        </Label>
        <LocationAutocomplete
          value={location}
          onChange={handleLocationChange}
          onSelect={handleLocationSelect}
          countryCodes={['gb']}
        />
      </FormGroup>

      <FormGroup>
        <Label>{t('high-rise')}</Label>

        <RadioGroup
          name="high-rise"
          value={!highRise}
          onChange={handleHighRiseChange}
        />
      </FormGroup>

      <FormGroup>
        <Label>{t('parking-difficult')}</Label>
        <Subtitle>{t('parking-difficult-info')}</Subtitle>

        <RadioGroup
          name="parking-difficult"
          value={parkingDifficult}
          onChange={handleParkingDifficultChange}
        />
      </FormGroup>

      <PriceSection>
        <PriceLabel>{t('price')}</PriceLabel>
        <PriceValue>{formattedPrice}</PriceValue>
      </PriceSection>

      <ButtonGroup>
        <PrimaryButton onClick={handleSaveAndClose}>
          {t('save-and-close')}
        </PrimaryButton>
        <TextButton onClick={handleClose}>{t('close')}</TextButton>
      </ButtonGroup>
    </ModalOverlay>
  )
}

InstallationModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSaveAndClose: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  cardType: PropTypes.number.isRequired,
  unitType: PropTypes.string.isRequired,
  priceText: PropTypes.string.isRequired,
  defmessage: PropTypes.string.isRequired,
  selection: PropTypes.shape({}),
}

export default InstallationModal
